import { z } from 'zod';
// Phase 17 (change-list 1.2) — care-professional work history.
//
// Replaces the reviewer's original "years of experience" single number with a
// structured list. The buyer-visible total is DERIVED from these rows (union of
// intervals, so concurrent jobs don't double-count) — never self-declared.
//
// Visibility: these rows (employer, comments) are caregiver + operator ONLY.
// Buyers see only the derived `totalYearsExperience` on
// BuyerVisibleCaregiverProfile. Never widen this shape into a buyer response.
//
// NOT an activation requirement — informational only.
/** Month-precision date, `YYYY-MM` (rows are stored as the 1st of the month). */
export const WorkExperienceMonthSchema = z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Expected a YYYY-MM month, e.g. 2024-03');
export const WORK_EXPERIENCE_COMMENTS_MAX = 1000;
export const WORK_EXPERIENCE_ROLE_TITLE_MAX = 120;
export const WORK_EXPERIENCE_EMPLOYER_MAX = 160;
/**
 * `endedOn` null = "Current" (still working there).
 * Shared by create + update so the date-ordering rule lives in ONE place.
 */
const workExperienceFields = {
    /** FREE TEXT by design — work history must capture reality ("Ward Clerk"),
     *  not our four platform roles. Matching/vetting never read this. */
    roleTitle: z.string().trim().min(1).max(WORK_EXPERIENCE_ROLE_TITLE_MAX),
    employer: z.string().trim().min(1).max(WORK_EXPERIENCE_EMPLOYER_MAX),
    startedOn: WorkExperienceMonthSchema,
    /** null = current. */
    endedOn: WorkExperienceMonthSchema.nullable().default(null),
    comments: z.string().trim().max(WORK_EXPERIENCE_COMMENTS_MAX).nullable().default(null),
};
/** `endedOn`, when present, must not precede `startedOn`. Mirrors the DB CHECK. */
function refineDateOrder(val, ctx) {
    if (typeof val.startedOn === 'string' &&
        typeof val.endedOn === 'string' &&
        val.endedOn < val.startedOn // YYYY-MM sorts lexicographically
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'End month cannot be before the start month.',
            path: ['endedOn'],
        });
    }
}
export const CreateWorkExperienceRequestSchema = z
    .object(workExperienceFields)
    .superRefine(refineDateOrder);
/** PATCH — every field optional, but the date-order rule still applies when
 *  both are supplied. The service re-checks against the persisted row so a
 *  partial update can't produce an inverted range. */
export const UpdateWorkExperienceRequestSchema = z
    .object({
    roleTitle: workExperienceFields.roleTitle.optional(),
    employer: workExperienceFields.employer.optional(),
    startedOn: WorkExperienceMonthSchema.optional(),
    endedOn: WorkExperienceMonthSchema.nullable().optional(),
    comments: z.string().trim().max(WORK_EXPERIENCE_COMMENTS_MAX).nullable().optional(),
})
    .superRefine(refineDateOrder);
export const WorkExperienceResponseSchema = z.object({
    id: z.string().uuid(),
    roleTitle: z.string(),
    employer: z.string(),
    startedOn: WorkExperienceMonthSchema,
    /** null = current. */
    endedOn: WorkExperienceMonthSchema.nullable(),
    comments: z.string().nullable(),
});
export const WorkExperienceListResponseSchema = z.object({
    /** Most-recent-first (current roles, then by start month descending). */
    items: z.array(WorkExperienceResponseSchema),
    /** Derived from the UNION of the intervals above — concurrent roles are
     *  merged, never double-counted. This is the ONE number buyers also see. */
    totalYearsExperience: z.number().int().min(0),
});
//# sourceMappingURL=work-experience.schema.js.map